import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { News } from '../models/news.interface';

@Injectable({
  providedIn: 'root'
})
export class ImageService {
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  private imageCache = new Map<string, { url: string; timestamp: number; webpUrl?: string }>();
  
  private fallbackImages: Record<string, string> = {
    'politica': 'assets/images/fallbacks/politica.jpg',
    'ciudad': 'assets/images/fallbacks/ciudad.jpg',
    'historias': 'assets/images/fallbacks/historias.jpg',
    'economia': 'assets/images/fallbacks/economia.jpg',
    'deportes': 'assets/images/fallbacks/deportes.jpg',
    'mundo': 'assets/images/fallbacks/mundo.jpg',
    'opinion': 'assets/images/fallbacks/opinion.jpg'
  };

  private defaultFallback = 'assets/images/fallbacks/default.jpg';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  /**
   * Obtiene la URL de la imagen para un artículo con caché optimizado
   * @param article El artículo del que se quiere obtener la imagen
   * @param preferWebP Si se prefiere formato WebP cuando esté disponible
   * @returns La URL de la imagen o una imagen de respaldo
   */
  getImageSrc(article: News | null | undefined, preferWebP: boolean = true): string | null {
    if (!article) return null;

    // Verificar si hay imágenes en el artículo
    const imageSource = article.imageUrl || article.imagePreview;

    // Si no hay imagen, usar una imagen de respaldo según la categoría
    if (!imageSource) {
      return this.getFallbackForCategory(article.category);
    }

    // Verificar caché primero
    const cacheKey = this.getCacheKey(article, imageSource);
    const cachedImage = this.getCachedImage(cacheKey);
    
    if (cachedImage) {
      return preferWebP && cachedImage.webpUrl ? cachedImage.webpUrl : cachedImage.url;
    }

    // Generar URL optimizada
    let optimizedUrl = this.generateOptimizedUrl(imageSource, article);
    let webpUrl: string | undefined;

    // Generar versión WebP si es posible
    if (preferWebP && this.canGenerateWebP(imageSource)) {
      webpUrl = this.generateWebPUrl(optimizedUrl);
    }

    // Guardar en caché
    this.setCachedImage(cacheKey, optimizedUrl, webpUrl);

    return preferWebP && webpUrl ? webpUrl : optimizedUrl;
  }

  private pickVersionDate(article: News): Date | null {
    const dates: Array<Date | undefined | null> = [
      (article as any).lastModified instanceof Date ? (article as any).lastModified : this.toDate((article as any).lastModified),
      article.publishDate instanceof Date ? article.publishDate : this.toDate((article as any).publishDate),
      article.createdAt instanceof Date ? article.createdAt : this.toDate((article as any).createdAt)
    ];
    return (dates.find(d => d instanceof Date && !isNaN((d as Date).getTime())) as Date) || null;
  }

  private toDate(value: any): Date | null {
    try {
      if (!value) return null;
      if (value instanceof Date) return value;
      // Firestore Timestamp
      if (value && typeof value.toDate === 'function') return value.toDate();
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  }

  private shouldBustCache(url: string): boolean {
    if (!url) return false;
    // Evitar tocar assets locales que ya controla el SW
    if (url.startsWith('assets/') || url.startsWith('/assets/')) return false;
    // Busteamos para Firebase Storage y URLs con CDN comunes
    return /firebasestorage\.googleapis\.com|storage\.googleapis\.com/i.test(url);
  }

  /**
   * Obtiene la imagen de respaldo para una categoría específica
   * @param category La categoría para la que se quiere obtener la imagen de respaldo
   * @returns La URL de la imagen de respaldo
   */
  getFallbackForCategory(category: string): string {
    return this.fallbackImages[category] || this.defaultFallback;
  }

  /**
   * Verifica si una imagen existe y se puede cargar
   * @param url La URL de la imagen a verificar
   */
  checkImageExists(url: string): void {
    if (!url || !isPlatformBrowser(this.platformId)) return;
    
    const img = new window.Image();
    img.src = url;
  }

  /**
   * Genera clave de caché para una imagen
   */
  private getCacheKey(article: News, imageSource: string): string {
    const versionDate = this.pickVersionDate(article);
    const version = versionDate ? versionDate.getTime() : 'default';
    return `${article.id || 'unknown'}_${version}_${btoa(imageSource).slice(0, 10)}`;
  }

  /**
   * Obtiene imagen desde caché si es válida
   */
  private getCachedImage(cacheKey: string): { url: string; webpUrl?: string } | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    
    const cached = this.imageCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      return { url: cached.url, webpUrl: cached.webpUrl };
    }
    
    // Limpiar caché expirado
    if (cached) {
      this.imageCache.delete(cacheKey);
    }
    
    return null;
  }

  /**
   * Guarda imagen en caché
   */
  private setCachedImage(cacheKey: string, url: string, webpUrl?: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    this.imageCache.set(cacheKey, {
      url,
      webpUrl,
      timestamp: Date.now()
    });
  }

  /**
   * Genera URL optimizada con parámetros de versión
   */
  private generateOptimizedUrl(imageSource: string, article: News): string {
    const versionDate = this.pickVersionDate(article);
    if (versionDate && this.shouldBustCache(imageSource)) {
      const ver = versionDate.getTime();
      const separator = imageSource.includes('?') ? '&' : '?';
      return `${imageSource}${separator}v=${ver}`;
    }
    return imageSource;
  }

  /**
   * Verifica si se puede generar versión WebP
   */
  private canGenerateWebP(url: string): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    
    // Verificar soporte WebP del navegador
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const webpSupported = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    
    // Solo para imágenes de Firebase Storage
    return webpSupported && /firebasestorage\.googleapis\.com|storage\.googleapis\.com/i.test(url);
  }

  /**
   * Genera URL WebP para Firebase Storage
   */
  private generateWebPUrl(originalUrl: string): string {
    // Para Firebase Storage, agregar parámetro de transformación
    if (/firebasestorage\.googleapis\.com/i.test(originalUrl)) {
      const separator = originalUrl.includes('?') ? '&' : '?';
      return `${originalUrl}${separator}format=webp&quality=85`;
    }
    return originalUrl;
  }

  /**
   * Limpia caché de imágenes expiradas
   */
  clearExpiredCache(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const now = Date.now();
    for (const [key, value] of this.imageCache.entries()) {
      if ((now - value.timestamp) >= this.CACHE_DURATION) {
        this.imageCache.delete(key);
      }
    }
  }

  /**
   * Limpia toda la caché de imágenes
   */
  clearAllCache(): void {
    this.imageCache.clear();
  }
}
